/**
 * The capsule player's own surface: the dark ground the shared question widgets
 * are drawn on before — and unless — a book theme paints over them.
 *
 * This is deliberately *not* an app appearance. Capsule play is an immersive,
 * video-first viewer, so it stays dark on a light device; that is the design, not
 * a leftover. It is also the base the FlashBook play palette falls back to when a
 * book has no theme (`defaultPalette` in `FlashBookPlayTheme`), which is why the
 * values are exactly the ones the widgets shipped with.
 *
 * It used to live as `EDITOR_COLORS` in `#/lib/slideTokens`, which was a lie on
 * two counts: it was named for the studios while the studios had all moved to the
 * studio chrome, and it sat in the *slide taxonomy* module next to `SLIDE_ACCENT`,
 * where a reader would reasonably expect a block's colour rather than a player's
 * backdrop. Studio furniture reads `#/themes-studio/useChrome`; play surfaces read
 * this; the taxonomy keeps only the taxonomy.
 *
 * The ramp, darkest → lightest:
 *
 *     page → surface → border → textFaint → textMuted → text   (ink on the page)
 *     #0A1220  #131F2E   #1E2D3D   #475569     #94A3B8     #FFFFFF
 */
export const PLAY_SURFACE = {
  /** The player's backdrop. */
  page: '#0a1220',
  /** A raised panel on that backdrop: a question card, an option row. */
  surface: '#131f2e',
  /** A hairline between panels. */
  border: '#1e2d3d',
  /** Primary ink: questions and option labels. */
  text: '#ffffff',
  /** Supporting copy that still has to read — an explanation under an answer. */
  textMuted: '#94a3b8',
  /** Quiet furniture: section captions, the word bank label. */
  textFaint: '#475569',
  /** The player's own accent, for the controls it draws itself. */
  accent: '#0085ff',
  /** Ink on `accent`. */
  onAccent: '#ffffff',
} as const
